import {useLazyQuery, useQuery} from '@apollo/client'
import axios from 'axios'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Alert, Badge, Button, CloseButton, Form} from 'react-bootstrap'
import {FormattedMessage, defineMessages, useIntl} from 'react-intl'
import {toast} from 'react-toastify'

import {
  TELEPHONY_DRAWER_CLOSE_EVENT,
  TELEPHONY_DRAWER_OPEN_EVENT,
  TELEPHONY_DRAWER_STATE_EVENT,
  TELEPHONY_DRAWER_STATE_REQUEST_EVENT,
  TELEPHONY_DRAWER_TOGGLE_EVENT,
  TELEPHONY_DRAWER_UPDATE_EVENT,
  TelephonyDrawerOpenDetail,
  TelephonyDrawerDiagnostics,
  TelephonyDrawerStateDetail,
  TELEPHONY_VICIDIAL_LOGOUT_EVENT,
  TELEPHONY_VICIDIAL_PREPARE_DIAL_EVENT,
  TelephonyVicidialPrepareDialDetail,
  TelephonyTransferAgentLive,
} from '../../types/telephony'
import {URL} from '../../gql/client'
import {GET_ALL_CONTACTS_PREVIEW} from '../../gql/queries/contactsApiQueries'
import {MY_OUTBOUND_CID_NUMBERS} from '../../gql/queries/twilioNumbers'
import {GET_ONE_LEAD} from '../../gql/queries/leadsQuery'
import {STORAGE_NAMES} from '../../helpers/consts'
import {useTelephony} from '../../hooks/useTelephony'
import useWebphoneOwnership from '../../hooks/useWebphoneOwnership'
import {IResContactsPreviewAPI, MemberPolicySummary, NodeContact} from '../../types/contactsApi'
import AudioDeviceCheck from './AudioDeviceCheck'
import './TelephonyDrawer.css'

const VICIDIAL_EXTERNAL_WINDOW_KEY = '__policytoolsVicidialWebphoneWindow'
const VICIDIAL_DRAWER_STANDALONE_QUERY_PARAM = 'vicidial_drawer'

const hasMeaningfulVicidialValue = (value?: string | number | null) => {
  const normalized = String(value || '')
    .trim()
    .toUpperCase()
  return Boolean(normalized && !['0', 'NONE', 'NULL', 'N/A'].includes(normalized))
}

const normalizePhoneDigits = (value?: string | number | null) =>
  String(value || '').replace(/\D+/g, '')

const phonesLikelyMatch = (left?: string | number | null, right?: string | number | null) => {
  const normalizedLeft = normalizePhoneDigits(left)
  const normalizedRight = normalizePhoneDigits(right)
  if (!normalizedLeft || !normalizedRight) return false
  if (normalizedLeft === normalizedRight) return true

  const localLeft = normalizedLeft.slice(-10)
  const localRight = normalizedRight.slice(-10)
  return Boolean(localLeft && localRight && localLeft === localRight)
}

const pickBestPolicyToolsContact = (
  contacts: NodeContact[],
  targetPhone: string
): NodeContact | null => {
  if (!contacts.length) return null
  const exactPhoneMatch = contacts.find((contact) => phonesLikelyMatch(contact.phone, targetPhone))
  return exactPhoneMatch || contacts[0] || null
}

const formatPolicyToolsLocation = (contact?: NodeContact | null) => {
  if (!contact) return ''
  return [contact.city, contact.stateEU?.name].filter(Boolean).join(', ')
}

const formatElapsedClock = (elapsedMs: number) => {
  const totalSeconds = Math.max(0, Math.floor(elapsedMs / 1000))
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  if (hours > 0) {
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(
      seconds
    ).padStart(2, '0')}`
  }

  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

const buildPolicyToolsPolicyLabel = (memberPolicy?: MemberPolicySummary | null) => {
  const policy = memberPolicy?.policy
  if (!policy) return ''

  const baseLabel =
    policy.policyName ||
    policy.typePolicy?.typePolicy ||
    policy.type ||
    policy.carrier?.name ||
    'Policy'
  const statusLabel = policy.status || policy.paymentStatus || ''
  return [baseLabel, statusLabel].filter(Boolean).join(' • ')
}

const computePolicyToolsAge = (dateBirth?: string | null): number | null => {
  if (!dateBirth) return null
  const dob = new Date(dateBirth)
  if (Number.isNaN(dob.getTime())) return null
  const now = new Date()
  let age = now.getFullYear() - dob.getFullYear()
  const monthDiff = now.getMonth() - dob.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) age -= 1
  return age >= 0 && age < 130 ? age : null
}

const policyToolsStatusTone = (status: string): 'completed' | 'pending' | 'neutral' => {
  const normalized = status.toUpperCase()
  if (/COMPLET|PROCES|ACTIV|APPROV|PAGAD/.test(normalized)) return 'completed'
  if (/PEND/.test(normalized)) return 'pending'
  return 'neutral'
}

const formatPolicyToolsPersonName = (
  person?: {firstName?: string | null; lastName?: string | null} | null
): string =>
  person
    ? [person.firstName, person.lastName]
        .map((part) => String(part || '').trim())
        .filter(Boolean)
        .join(' ')
    : ''

const isDispositionPendingState = (status?: string | null, realTimeSubStatus?: string | null) => {
  const statusValue = String(status || '')
    .trim()
    .toUpperCase()
  const subStatusValue = String(realTimeSubStatus || '')
    .trim()
    .toUpperCase()
  return (
    statusValue === 'DEAD' ||
    subStatusValue === 'DEAD' ||
    subStatusValue.includes('DEAD') ||
    statusValue === 'DISPO' ||
    subStatusValue === 'DISPO'
  )
}

// Audible incoming-call alert (Web Audio — no asset needed). The agent has already
// interacted with the page (login/dial), so the AudioContext is allowed to play.
const playIncomingCallAlert = () => {
  try {
    const Ctx = window.AudioContext || (window as any).webkitAudioContext
    if (!Ctx) return
    const ctx = new Ctx()
    const ring = (offset: number) => {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.type = 'sine'
      osc.frequency.value = 1000
      osc.connect(gain)
      gain.connect(ctx.destination)
      const t = ctx.currentTime + offset
      gain.gain.setValueAtTime(0.0001, t)
      gain.gain.exponentialRampToValueAtTime(0.3, t + 0.02)
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.4)
      osc.start(t)
      osc.stop(t + 0.42)
    }
    ring(0)
    ring(0.5)
    ring(1.0)
    window.setTimeout(() => ctx.close().catch(() => {}), 2200)
  } catch {
    // best-effort — ignore audio errors
  }
}

// Manual campaign ("pausado-hasta-contestar"): campaign id + the pause code used to
// park the agent between calls (so inbound waits until they press Answer).
const MANUAL_CAMPAIGN_ID = '0001'
const MANUAL_INBOUND_PAUSE_CODE = 'INBND'

// Format a raw inbound caller number (e.g. "13053226556" / "3053226556") for the
// ring banner as "(305) 322-6556"; falls back to the raw value for non-NANP numbers.
const formatCallerId = (raw?: string): string => {
  const digits = String(raw || '').replace(/\D/g, '')
  const ten = digits.length === 11 && digits[0] === '1' ? digits.slice(1) : digits
  if (ten.length === 10) return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`
  return String(raw || '').trim()
}

// Live-availability dot for a transfer target agent:
// green = available, amber = logged in but on a call, gray = offline.
const transferAgentStatusSymbol = (live: TelephonyTransferAgentLive): string => {
  if (!live.logged_in) return '⚪'
  return live.available ? '🟢' : '🟡'
}

interface VicidialStandaloneDrawerSnapshot {
  drawerState: TelephonyDrawerOpenDetail
  embeddedWebphoneUrl?: string
  pauseCodeInput?: string
  manualPhoneNumber?: string
  manualLeadId?: number | string | null
}

const SIGNAL_BAR_FILL: Record<'good' | 'fair' | 'poor' | 'idle', number> = {good: 4, fair: 2, poor: 1, idle: 0}

// Cell-signal style bars for the agent's connection quality:
// good = 4 green bars, fair = 2 yellow bars, poor = 1 red bar.
const SignalBars = ({quality}: {quality: 'good' | 'fair' | 'poor' | 'idle'}) => {
  const filled = SIGNAL_BAR_FILL[quality] ?? 1
  return (
    <span className={`telephony-signal telephony-signal--${quality}`} aria-hidden='true'>
      {[1, 2, 3, 4].map((bar) => (
        <span key={bar} className={`telephony-signal__bar${bar <= filled ? ' is-on' : ''}`} />
      ))}
    </span>
  )
}

/** Separador de las listas de contexto y `n/a` de la calidad. Simbolos. */
const DOT_SEP = ' · '
const NOT_AVAILABLE = 'n/a'
/** Unidades del indicador de calidad. Simbolos. */
const PCT = '%'
const MS = 'ms'
/** Icono de la cola en el selector de transferencia. */
const GLYPH_PHONE = '📞'

const M = defineMessages({
  // ── Cabecera ──
  audioCheckTooltip: {
    id: 'telephony.drawer.audioCheckTooltip',
    defaultMessage: 'Test your microphone and speaker',
  },
  audio: {id: 'telephony.drawer.audio', defaultMessage: 'Audio'},
  undock: {id: 'telephony.drawer.undock', defaultMessage: 'Undock'},
  loggingOut: {id: 'telephony.drawer.loggingOut', defaultMessage: 'Logging out...'},
  logout: {id: 'telephony.supervisor.live.logout', defaultMessage: 'Logout'},
  close: {id: 'common.action.close', defaultMessage: 'Close'},

  // ── Aviso de pestaña segura ──
  secureTabRequired: {
    id: 'telephony.drawer.secureTabRequired',
    defaultMessage: 'Secure tab required',
  },
  secureTabBody: {
    id: 'telephony.drawer.secureTabBody',
    defaultMessage:
      'Your current app session is running over HTTP. ViciPhone rejects embedded access from non-encrypted referrers like <code>localhost</code>.',
  },
  openSecureWebphone: {
    id: 'telephony.drawer.openSecureWebphone',
    defaultMessage: 'Open secure webphone',
  },

  // ── Resumen de sesión ──
  sessionOverview: {
    id: 'telephony.drawer.sessionOverview',
    defaultMessage: 'Session overview',
  },
  waitingForCalls: {
    id: 'telephony.drawer.waitingForCalls',
    defaultMessage: 'WAITING FOR CALLS',
  },
  statusPending: {id: 'telephony.drawer.statusPending', defaultMessage: 'PENDING'},
  youArePaused: {id: 'telephony.drawer.youArePaused', defaultMessage: 'YOU ARE PAUSED'},
  youAreActive: {id: 'telephony.drawer.youAreActive', defaultMessage: 'YOU ARE ACTIVE'},
  sessionNotReady: {
    id: 'telephony.drawer.sessionNotReady',
    defaultMessage: 'SESSION NOT READY',
  },
  agent: {id: 'common.field.agent', defaultMessage: 'Agent'},
  campaign: {id: 'telephony.field.campaign', defaultMessage: 'Campaign'},
  session: {id: 'telephony.drawer.session', defaultMessage: 'Session'},
  callerId: {id: 'telephony.drawer.callerId', defaultMessage: 'Caller ID'},
  callerIdTooltip: {
    id: 'telephony.drawer.callerIdTooltip',
    defaultMessage: 'Outbound Caller ID for this call',
  },
  inboundGroup: {id: 'telephony.drawer.inboundGroup', defaultMessage: 'Inbound Group'},
  directAgent: {id: 'telephony.drawer.directAgent', defaultMessage: 'Direct Agent'},
  defaultSuffix: {id: 'telephony.drawer.defaultSuffix', defaultMessage: ' (default)'},

  // ── Calidad de conexión ──
  connectionTooltip: {
    id: 'telephony.drawer.connectionTooltip',
    defaultMessage:
      'Your live connection quality — codec {codec}, packet loss {loss}%, jitter {jitter}ms. Green = good, yellow = fair, red = poor.',
  },
  idle: {id: 'telephony.supervisor.audioQuality.idle', defaultMessage: 'Idle'},
  noActiveCall: {id: 'telephony.drawer.noActiveCall', defaultMessage: 'no active call'},
  noAudioConnectionTooltip: {
    id: 'telephony.drawer.noAudioTooltip',
    defaultMessage:
      'No, it is not possible to establish an audio connection; please check your internet connection and try again.',
  },
  noAudioConnection: {
    id: 'telephony.drawer.noAudioConnection',
    defaultMessage: 'No audio connection',
  },

  // ── Etiquetas de contexto ──
  pauseCodeChip: {id: 'telephony.drawer.chip.pauseCode', defaultMessage: 'Pause code: {code}'},
  queueChip: {id: 'telephony.drawer.chip.queue', defaultMessage: 'Queue: {count}'},
  phoneChip: {id: 'telephony.drawer.chip.phone', defaultMessage: 'Phone: {value}'},
  emailChip: {id: 'telephony.drawer.chip.email', defaultMessage: 'Email: {value}'},
  locationChip: {id: 'telephony.drawer.chip.location', defaultMessage: 'Location: {value}'},
  leadChip: {id: 'telephony.drawer.chip.lead', defaultMessage: 'Lead: {value}'},
  leadIdChip: {id: 'telephony.drawer.chip.leadId', defaultMessage: 'Lead ID: {value}'},
  morePolicies: {
    id: 'telephony.drawer.morePolicies',
    defaultMessage: '+{count} more policies',
  },
  dialTarget: {id: 'telephony.drawer.dialTarget', defaultMessage: 'Dial target'},
  policyToolsContact: {
    id: 'telephony.drawer.policyToolsContact',
    defaultMessage: 'PolicyTools contact',
  },
  policyToolsContactReady: {
    id: 'telephony.drawer.contactReady',
    defaultMessage: 'PolicyTools contact ready',
  },
  callingThrough: {
    id: 'telephony.drawer.callingThrough',
    defaultMessage: 'Calling through PolicyDial',
  },
  numberReady: {id: 'telephony.drawer.numberReady', defaultMessage: 'Selected number ready'},
  advancedDiagnostics: {
    id: 'telephony.drawer.advancedDiagnostics',
    defaultMessage: 'Advanced diagnostics',
  },

  // ── Llamada en curso ──
  liveConversation: {
    id: 'telephony.drawer.liveConversation',
    defaultMessage: 'Live conversation',
  },
  onCall: {id: 'telephony.drawer.onCall', defaultMessage: 'On call'},
  hangingUp: {id: 'telephony.drawer.hangingUp', defaultMessage: 'Hanging up...'},
  hangUp: {id: 'telephony.drawer.hangUp', defaultMessage: 'Hang up'},
  caller: {id: 'telephony.drawer.caller', defaultMessage: 'Caller'},
  vicidialLead: {id: 'telephony.drawer.vicidialLead', defaultMessage: 'VICIdial lead'},
  phone: {id: 'common.field.phone', defaultMessage: 'Phone'},
  age: {id: 'common.field.age', defaultMessage: 'Age'},
  activePolicy: {id: 'telephony.drawer.activePolicy', defaultMessage: 'Active policy'},
  yes: {id: 'common.boolean.yes', defaultMessage: 'Yes'},
  no: {id: 'common.boolean.no', defaultMessage: 'No'},
  location: {id: 'common.field.location', defaultMessage: 'Location'},
  assignedTo: {id: 'policies.list.filters.field.assignedTo', defaultMessage: 'Assigned to'},
  leadId: {id: 'telephony.drawer.leadId', defaultMessage: 'Lead ID'},
  vendorId: {id: 'telephony.drawer.vendorId', defaultMessage: 'Vendor ID'},
  email: {id: 'common.field.email', defaultMessage: 'Email'},
  notesLabel: {id: 'telephony.drawer.notesLabel', defaultMessage: 'Notes:'},
  policiesCount: {
    id: 'telephony.drawer.policiesCount',
    defaultMessage: 'Policies · {count}',
  },
  notInPolicyTools: {
    id: 'telephony.drawer.notInPolicyTools',
    defaultMessage: '<b>Not in PolicyTools.</b> No contact or history.',
  },

  // ── Cierre de llamada ──
  wrapUp: {id: 'telephony.drawer.wrapUp', defaultMessage: 'Wrap-up'},
  wrapUpNote: {
    id: 'telephony.drawer.wrapUpNote',
    defaultMessage: 'Submit a disposition to release the call.',
  },

  // ── Llamada entrante ──
  incomingCall: {id: 'telephony.drawer.incomingCall', defaultMessage: 'Incoming call'},
  callsWaiting: {
    id: 'telephony.drawer.callsWaiting',
    defaultMessage: '{count} calls waiting · press Answer',
  },
  incomingPressAnswer: {
    id: 'telephony.drawer.incomingPressAnswer',
    defaultMessage: 'Incoming call · press Answer to take it',
  },
  pressAnswer: {id: 'telephony.drawer.pressAnswer', defaultMessage: 'Press Answer to take it'},
  connecting: {id: 'telephony.drawer.connecting', defaultMessage: 'Connecting…'},
  answer: {id: 'telephony.drawer.answer', defaultMessage: 'Answer'},

  // ── Controles del agente ──
  agentControls: {id: 'telephony.drawer.agentControls', defaultMessage: 'Agent controls'},
  agentControlsSub: {
    id: 'telephony.drawer.agentControlsSub',
    defaultMessage: 'Pause, resume, and transfer',
  },
  inQueue: {id: 'telephony.drawer.inQueue', defaultMessage: '{count} in queue'},
  pauseReason: {id: 'telephony.drawer.pauseReason', defaultMessage: 'Pause reason'},
  selectReason: {id: 'telephony.drawer.selectReason', defaultMessage: 'Select a reason…'},
  dialNextNumber: {
    id: 'telephony.drawer.dialNextNumber',
    defaultMessage: 'DIAL NEXT NUMBER',
  },
  activePause: {id: 'telephony.drawer.activePause', defaultMessage: 'ACTIVE PAUSE'},
  goingActive: {id: 'telephony.drawer.goingActive', defaultMessage: 'GOING ACTIVE...'},
  pausing: {id: 'telephony.drawer.pausing', defaultMessage: 'PAUSING...'},

  // ── Transferencia ──
  transferCall: {id: 'telephony.drawer.transferCall', defaultMessage: 'Transfer call'},
  refreshing: {id: 'telephony.drawer.refreshing', defaultMessage: 'Refreshing…'},
  refresh: {id: 'common.action.refresh', defaultMessage: 'Refresh'},
  selectDestination: {
    id: 'telephony.drawer.selectDestination',
    defaultMessage: 'Select destination…',
  },
  agents: {id: 'common.field.agents', defaultMessage: 'Agents'},
  queues: {id: 'telephony.drawer.queues', defaultMessage: 'Queues'},
  agentOffline: {id: 'telephony.drawer.agentOffline', defaultMessage: ' (offline)'},
  agentOnCall: {id: 'telephony.drawer.agentOnCall', defaultMessage: ' (on a call)'},
  sending: {id: 'telephony.drawer.sending', defaultMessage: 'Sending...'},
  transfer: {id: 'telephony.drawer.transfer', defaultMessage: 'Transfer'},
  loadingDestinations: {
    id: 'telephony.drawer.loadingDestinations',
    defaultMessage: 'Loading destinations…',
  },
  noDestinations: {
    id: 'telephony.drawer.noDestinations',
    defaultMessage: 'No transfer destinations available.',
  },

  // ── Marcación manual ──
  manualDial: {id: 'telephony.drawer.manualDial', defaultMessage: 'Manual dial'},
  numberToDial: {id: 'telephony.drawer.numberToDial', defaultMessage: 'Number to dial'},
  calling: {id: 'telephony.drawer.calling', defaultMessage: 'Calling…'},
  call: {id: 'leads.call.action', defaultMessage: 'Call'},

  // ── Marco del webphone ──
  webphone: {id: 'telephony.drawer.webphone', defaultMessage: 'Webphone'},
  embedded: {id: 'telephony.drawer.embedded', defaultMessage: 'Embedded'},
  live: {id: 'telephony.drawer.live', defaultMessage: 'Live'},
  otherTabTitle: {
    id: 'telephony.drawer.otherTabTitle',
    defaultMessage: 'PolicyDial is active in another tab',
  },
  otherTabCopy: {
    id: 'telephony.drawer.otherTabCopy',
    defaultMessage:
      'Your phone can only run in one tab at a time, otherwise calls collide. It is running in another PolicyTools tab right now.',
  },
  useItHere: {id: 'telephony.drawer.useItHere', defaultMessage: 'Use it here'},
  finishOtherCall: {
    id: 'telephony.drawer.finishOtherCall',
    defaultMessage: 'Finish the active call in the other tab first.',
  },
  embeddedWebphoneTitle: {
    id: 'telephony.drawer.embeddedWebphoneTitle',
    defaultMessage: 'PolicyDial Embedded Webphone',
  },
  keypadDisabled: {
    id: 'telephony.drawer.keypadDisabled',
    defaultMessage: 'The webphone keypad is disabled — dial from PolicyTools.',
  },
  waitingForWebphone: {
    id: 'telephony.drawer.waitingForWebphone',
    defaultMessage: 'Waiting for webphone',
  },
  waitingForWebphoneCopy: {
    id: 'telephony.drawer.waitingForWebphoneCopy',
    defaultMessage:
      'Open or reconnect the telephony session to load the embedded workspace here.',
  },

  // ── Motivos de pausa. El `code` viaja a PolicyDial. ──
  pauseBath: {id: 'telephony.drawer.pause.bath', defaultMessage: 'Bathroom'},
  pauseLunch: {id: 'telephony.drawer.pause.lunch', defaultMessage: 'Lunch'},
  pauseShort: {id: 'telephony.drawer.pause.short', defaultMessage: 'Short break'},
  pauseLong: {id: 'telephony.drawer.pause.long', defaultMessage: 'Long break'},

  // ── Avisos ──
  toastIncoming: {
    id: 'telephony.drawer.toast.incoming',
    defaultMessage: '📞 Incoming call — please answer',
  },
  tabIncoming: {id: 'telephony.drawer.tabIncoming', defaultMessage: '📞 INCOMING CALL'},
  errSessionExpired: {
    id: 'telephony.drawer.error.sessionExpired',
    defaultMessage: 'Your session expired. Log in again to disconnect PolicyDial.',
  },
  errSelectPause: {
    id: 'telephony.drawer.error.selectPause',
    defaultMessage: 'Select a pause reason before pausing.',
  },
  errSelectTransfer: {
    id: 'telephony.drawer.error.selectTransfer',
    defaultMessage: 'Select a transfer destination first.',
  },
  errInvalidTransfer: {
    id: 'telephony.drawer.error.invalidTransfer',
    defaultMessage: 'Select a valid transfer destination.',
  },
  errEnterNumber: {
    id: 'telephony.drawer.error.enterNumber',
    defaultMessage: 'Enter a phone number first.',
  },
  errConnectFirst: {
    id: 'telephony.drawer.error.connectFirst',
    defaultMessage: 'Connect PolicyDial from Integrations before starting a call.',
  },
  disconnected: {
    id: 'telephony.drawer.disconnected',
    defaultMessage: 'PolicyDial disconnected.',
  },
  disconnectedSecureTab: {
    id: 'telephony.drawer.disconnectedSecureTab',
    defaultMessage:
      'PolicyDial disconnected. If the secure PolicyDial page still remains open in another browser tab, use the native LOGOUT link there once.',
  },
  errLogout: {
    id: 'telephony.drawer.error.logout',
    defaultMessage: 'Unable to log out from PolicyDial.',
  },
})

export const TelephonyDrawer = () => {
  const intl = useIntl()
  const telephony = useTelephony()

  // Tell the backend that THIS browser hosts the webphone (holds the PolicyDial
  // WebSocket / call audio). Cached separately from the general app browser so a
  // second tab/browser (e.g. Chrome for the rest of PolicyTools) can't overwrite
  // it — the supervisor "Browser" column then reflects where the audio really is.
  const reportWebphoneBrowser = useCallback(() => {
    const token = localStorage.getItem(STORAGE_NAMES.TOKEN)
    if (!token) return
    axios
      .post(
        `${URL}/api/telephony/webphone-browser`,
        {},
        {headers: {Authorization: `Bearer ${token}`}}
      )
      .catch(() => {
        /* best-effort */
      })
  }, [])

  const [show, setShow] = useState(false)
  const [collapsed, setCollapsed] = useState(false)
  const [showAudioCheck, setShowAudioCheck] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  const [embeddedWebphoneUrl, setEmbeddedWebphoneUrl] = useState('')

  // Keep the webphone-browser mark fresh while the embedded webphone is open
  // (cache TTL is ~1h; refresh well within it).
  useEffect(() => {
    if (!embeddedWebphoneUrl) return
    reportWebphoneBrowser()
    const id = setInterval(reportWebphoneBrowser, 15 * 60 * 1000)
    return () => clearInterval(id)
  }, [embeddedWebphoneUrl, reportWebphoneBrowser])

  const [pauseCodeInput, setPauseCodeInput] = useState('')
  const [manualPhoneNumber, setManualPhoneNumber] = useState('')
  const [manualLeadId, setManualLeadId] = useState<number | string | null>(null)
  const [manualAutoDialPending, setManualAutoDialPending] = useState(false)

  // PolicyDial per-call outbound Caller ID: the agent picks which number to dial out
  // with — their own personal number (Direct Agent) or a number of a group they belong
  // to (Inbound Group). Sourced from myOutboundCidNumbers (their ASSIGNED personal
  // numbers + group numbers — the exact set start_call accepts), and scoped to the
  // in-groups they selected at Connect. Blank = their personal default (is_outbound_cid);
  // a group number is opt-in per call and never the default.
  const [selectedCid, setSelectedCid] = useState<string>('')
  const {data: myOutboundCidData} = useQuery(MY_OUTBOUND_CID_NUMBERS, {
    fetchPolicy: 'cache-and-network',
  })
  const selectedInboundGroups = telephony.selectedVicidialInboundGroups
  const cidOptions = useMemo(() => {
    type CidNumber = {
      e164: string
      friendlyName?: string
      status?: string
      routeMode?: string
      isOutboundCid?: boolean
      assignedGroupName?: string
      assignedGroupIngroupId?: string
    }
    const nums = ((myOutboundCidData?.myOutboundCidNumbers || []) as CidNumber[]).filter(
      (n) => n.status === 'ACTIVE'
    )
    // Scope to the in-groups selected at Connect: a personal number shows when AGENTDIRECT
    // is selected; a group number shows when its own in-group is selected. If the selection
    // isn't loaded yet, show all so the agent is never left without a CID.
    const selected = selectedInboundGroups || []
    const scoped = selected.length
      ? nums.filter((n) =>
          n.routeMode === 'GROUP'
            ? !!n.assignedGroupIngroupId && selected.includes(n.assignedGroupIngroupId)
            : selected.includes('AGENTDIRECT')
        )
      : nums
    // Personal first, then group; dedupe by number.
    const ordered = [
      ...scoped.filter((n) => n.routeMode !== 'GROUP'),
      ...scoped.filter((n) => n.routeMode === 'GROUP'),
    ]
    const seen = new Set<string>()
    return ordered.filter((n) => {
      if (!n.e164 || seen.has(n.e164)) return false
      seen.add(n.e164)
      return true
    })
  }, [myOutboundCidData, selectedInboundGroups])
  useEffect(() => {
    if (!cidOptions.length) return
    if (!cidOptions.some((n) => n.e164 === selectedCid)) {
      // Default preselect = the PERSONAL number flagged is_outbound_cid (a group number
      // is never the default). Fall back to the first personal number, then anything.
      const def =
        cidOptions.find((n) => n.isOutboundCid && n.routeMode !== 'GROUP') ||
        cidOptions.find((n) => n.routeMode !== 'GROUP') ||
        cidOptions[0]
      setSelectedCid(def.e164)
    }
  }, [cidOptions, selectedCid])
  // Transfer picker selection, encoded as "AGENT:<extension>" or "INGROUP:<group_id>".
  const [transferDestination, setTransferDestination] = useState('')
  const [runningAction, setRunningAction] = useState('')
  const [nowTick, setNowTick] = useState(() => Date.now())
  const [policyToolsContact, setPolicyToolsContact] = useState<NodeContact | null>(null)
  const [policyToolsLead, setPolicyToolsLead] = useState<{
    id?: string
    name?: string
    phone?: string
    email?: string
    contact?: {firstName?: string; lastName?: string; phone?: string; email?: string} | null
  } | null>(null)
  const wasPausedRef = useRef(false)
  const preparedDialLifecycleSeenRef = useRef(false)
  const policyToolsLookupPhoneRef = useRef('')
  const policyToolsLookupLeadIdRef = useRef('')
  const wasInCallRef = useRef(false)
  // Timestamp when the agent's channel went without measurable media, so we only
  // warn about "no audio connection" after it's been sustained (avoids false alarms
  // on transient nulls right after login / between poll cycles).
  const noMediaSinceRef = useRef<number | null>(null)
  const [lookupPolicyToolsContact] = useLazyQuery<IResContactsPreviewAPI>(
    GET_ALL_CONTACTS_PREVIEW,
    {
      fetchPolicy: 'no-cache',
    }
  )
  const [lookupPolicyToolsLead] = useLazyQuery(GET_ONE_LEAD, {
    fetchPolicy: 'no-cache',
  })

  const wrapUpStartedAt = telephony.vicidialSession.wrapUpStartedAt
  const liveCallStartedAt = telephony.vicidialSession.callStartedAt
  useEffect(() => {
    if (!wrapUpStartedAt && !liveCallStartedAt) return
    setNowTick(Date.now())
    const id = window.setInterval(() => setNowTick(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [liveCallStartedAt, wrapUpStartedAt])

  // Alert the agent the moment a call lands — audible ring + toast + tab-title flash
  // — so they engage immediately and the customer doesn't hit dead air and hang up.
  const prevCallAlertRef = useRef<number | null>(null)
  useEffect(() => {
    const cur = liveCallStartedAt
    if (cur && cur !== prevCallAlertRef.current) {
      prevCallAlertRef.current = cur
      // Only for a genuinely fresh arrival, not a call already in progress on reload.
      if (Date.now() - cur < 8000) {
        playIncomingCallAlert()
        toast.info(intl.formatMessage(M.toastIncoming), {
          autoClose: 6000,
          toastId: 'incoming-call',
        })
        const original = document.title
        const incomingTitle = intl.formatMessage(M.tabIncoming)
        document.title = incomingTitle
        window.setTimeout(() => {
          if (document.title === incomingTitle) document.title = original
        }, 6000)
      }
    } else if (!cur) {
      prevCallAlertRef.current = null
    }
  }, [liveCallStartedAt])
  const [drawerState, setDrawerState] = useState<TelephonyDrawerOpenDetail>({
    url: '',
    title: '',
  })

  const isVicidialAutologinBridge = useMemo(() => {
    return (
      drawerState.provider === 'VICIDIAL' &&
      drawerState.url.includes('/api/telephony/vicidial/autologin')
    )
  }, [drawerState.provider, drawerState.url])

  const shouldForceExternalOpen = useMemo(() => {
    if (!drawerState.url || typeof window === 'undefined') return false
    return window.location.protocol !== 'https:' && drawerState.url.startsWith('https://')
  }, [drawerState.url])

  const isAgentScreenMode = useMemo(() => {
    return drawerState.provider === 'VICIDIAL' && drawerState.displayMode === 'agent'
  }, [drawerState.displayMode, drawerState.provider])

  const canOperateVicidial = useMemo(() => {
    return (
      drawerState.provider === 'VICIDIAL' &&
      !isAgentScreenMode &&
      telephony.config.vicidial_enabled &&
      Boolean(telephony.config.vicidial_agent_user)
    )
  }, [
    drawerState.provider,
    isAgentScreenMode,
    telephony.config.vicidial_agent_user,
    telephony.config.vicidial_enabled,
  ])

  const publishDrawerState = useCallback(
    (mounted: boolean, visible: boolean, provider?: TelephonyDrawerStateDetail['provider']) => {
      window.dispatchEvent(
        new CustomEvent<TelephonyDrawerStateDetail>(TELEPHONY_DRAWER_STATE_EVENT, {
          detail: {
            mounted,
            visible,
            provider,
          },
        })
      )
    },
    []
  )

  const openInNewTab = () => {
    if (drawerState.provider === 'VICIDIAL' && drawerState.url) {
      const snapshot: VicidialStandaloneDrawerSnapshot = {
        drawerState,
        embeddedWebphoneUrl,
        pauseCodeInput,
        manualPhoneNumber,
        manualLeadId,
      }
      localStorage.setItem(STORAGE_NAMES.VICIDIAL_DRAWER_STANDALONE_STATE, JSON.stringify(snapshot))

      const targetLocation = new window.URL(window.location.href)
      targetLocation.searchParams.set(VICIDIAL_DRAWER_STANDALONE_QUERY_PARAM, '1')
      const externalWindow = window.open(targetLocation.toString(), '_blank')
      externalWindow?.focus?.()
      return
    }

    const targetUrl = isVicidialAutologinBridge
      ? `${drawerState.url}${drawerState.url.includes('?') ? '&' : '?'}standalone=1`
      : embeddedWebphoneUrl || drawerState.url
    if (!targetUrl) return
    const externalWindow = window.open(
      targetUrl,
      isVicidialAutologinBridge ? '_blank' : 'PolicyToolsVicidialWebphone'
    )
    if (externalWindow) {
      if (!isVicidialAutologinBridge) {
        ;(window as any)[VICIDIAL_EXTERNAL_WINDOW_KEY] = externalWindow
      }
      externalWindow.focus?.()
    }
  }

  const closeTrackedExternalWindow = () => {
    let externalWindow = (window as any)[VICIDIAL_EXTERNAL_WINDOW_KEY] as Window | null | undefined
    if (!externalWindow) {
      externalWindow = window.open('', 'PolicyToolsVicidialWebphone')
    }
    if (
      externalWindow &&
      !externalWindow.closed &&
      externalWindow.location.href !== 'about:blank'
    ) {
      externalWindow.close()
    }
    ;(window as any)[VICIDIAL_EXTERNAL_WINDOW_KEY] = null
  }

  const handleVicidialLogout = async () => {
    const token = localStorage.getItem(STORAGE_NAMES.TOKEN)
    if (!token) {
      toast.error(intl.formatMessage(M.errSessionExpired))
      return
    }

    setDisconnecting(true)
    try {
      await axios.post(
        `${URL}/api/telephony/disconnect-agent`,
        {provider: 'VICIDIAL'},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      )

      localStorage.removeItem(STORAGE_NAMES.VICIDIAL_SESSION)
      localStorage.setItem(STORAGE_NAMES.VICIDIAL_FORCE_DISCONNECTED, '1')
      window.dispatchEvent(new CustomEvent(TELEPHONY_VICIDIAL_LOGOUT_EVENT))

      const channel = new BroadcastChannel('policytools_vicidial_channel')
      channel.postMessage('CLOSE_WEBPHONE')
      channel.close()

      closeTrackedExternalWindow()
      setCollapsed(false)
      setShow(false)
      toast.info(
        intl.formatMessage(
          shouldForceExternalOpen ? M.disconnectedSecureTab : M.disconnected
        )
      )
    } catch (error: any) {
      const message =
        error?.response?.data?.error || intl.formatMessage(M.errLogout)
      toast.error(message)
    } finally {
      setDisconnecting(false)
    }
  }

  const runVicidialDrawerAction = async (actionKey: string, action: () => Promise<unknown>) => {
    setRunningAction(actionKey)
    try {
      await action()
    } finally {
      setRunningAction('')
    }
  }

  const handlePauseAgent = async () => {
    if (!pauseCodeInput.trim()) {
      toast.error(intl.formatMessage(M.errSelectPause))
      return
    }
    await runVicidialDrawerAction('pause', () => telephony.setVicidialPause(true, pauseCodeInput))
  }

  const handleResumeAgent = async () => {
    await runVicidialDrawerAction('resume', () => telephony.setVicidialPause(false))
    // Reset the reason so the next pause forces a fresh, conscious selection.
    setPauseCodeInput('')
  }

  // NOTE: the incoming ring SOUND (looping phone tone) is owned by the floating button
  // (TelephonyFloatingButton), which is always mounted regardless of drawer visibility —
  // so the ring alerts the agent even with the drawer closed or on another tab. The
  // drawer only renders the visual "WAITING FOR CALLS" / Answer UI; no sound here (would
  // double-ring).

  // Manual campaign "pausado-hasta-contestar": park the agent (paused) whenever they
  // go idle so inbound calls WAIT for a manual Answer instead of auto-connecting.
  // Guards avoid pausing mid-dial or while a call is being delivered.
  const autoPauseTimerRef = useRef<number | null>(null)
  useEffect(() => {
    const s = telephony.vicidialSession
    const status = String(s.status || '').toUpperCase()
    // In a closer/blended campaign the idle-available state is READY or CLOSER.
    const available = status === 'READY' || status === 'CLOSER'
    const inCall = status === 'INCALL' || Boolean(String(s.callId || '').trim())
    const idle =
      String(s.campaignId || '') === MANUAL_CAMPAIGN_ID &&
      s.activeSession &&
      available &&
      !inCall &&
      s.pendingInboundCount === 0 &&
      runningAction !== 'dial' &&
      runningAction !== 'resume' &&
      !manualAutoDialPending
    if (idle && autoPauseTimerRef.current == null) {
      autoPauseTimerRef.current = window.setTimeout(() => {
        autoPauseTimerRef.current = null
        void telephony.setVicidialPause(true, MANUAL_INBOUND_PAUSE_CODE)
      }, 1500)
    }
    if (!idle && autoPauseTimerRef.current != null) {
      window.clearTimeout(autoPauseTimerRef.current)
      autoPauseTimerRef.current = null
    }
    return () => {
      if (autoPauseTimerRef.current != null) {
        window.clearTimeout(autoPauseTimerRef.current)
        autoPauseTimerRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    telephony.vicidialSession.campaignId,
    telephony.vicidialSession.status,
    telephony.vicidialSession.callId,
    telephony.vicidialSession.pendingInboundCount,
    telephony.vicidialSession.activeSession,
    runningAction,
    manualAutoDialPending,
  ])

  const handleTransferCall = async () => {
    const raw = transferDestination.trim()
    if (!raw) {
      toast.error(intl.formatMessage(M.errSelectTransfer))
      return
    }
    const separatorIndex = raw.indexOf(':')
    const type = separatorIndex >= 0 ? raw.slice(0, separatorIndex) : ''
    const value = separatorIndex >= 0 ? raw.slice(separatorIndex + 1) : ''
    if (!value) {
      toast.error(intl.formatMessage(M.errInvalidTransfer))
      return
    }
    // i18n-ok: valor del modelo, viaja a la API
    const destinationType = type === 'INGROUP' ? 'INGROUP' : 'AGENT'
    const response = await telephony.transferVicidialCall(value, destinationType)
    if (response) {
      setTransferDestination('')
    }
  }

  const handleHangupCurrentCall = async () => {
    await runVicidialDrawerAction('hangup', () =>
      telephony.hangupCall('VICIDIAL', telephony.vicidialSession.localCallId || null)
    )
  }

  const handleManualCall = useCallback(async () => {
    const phoneNumber = manualPhoneNumber.trim()
    if (!phoneNumber) {
      toast.error(intl.formatMessage(M.errEnterNumber))
      return
    }

    await runVicidialDrawerAction('dial', async () => {
      await telephony.startCall({
        phone: phoneNumber,
        leadId: manualLeadId,
        providerOverride: 'VICIDIAL',
        autoOpenWebphone: false,
        outboundCid: selectedCid || undefined,
      })
    })
  }, [manualLeadId, manualPhoneNumber, selectedCid, telephony])

  useEffect(() => {
    const currentStatus = String(telephony.vicidialSession.status || '')
      .trim()
      .toUpperCase()
    const currentPauseCode = String(telephony.vicidialSession.pauseCode || '')
      .trim()
      .toUpperCase()
    const currentlyPaused = currentStatus === 'PAUSED'

    if (currentPauseCode && currentPauseCode !== MANUAL_INBOUND_PAUSE_CODE) {
      setPauseCodeInput(currentPauseCode)
    } else if (currentPauseCode === MANUAL_INBOUND_PAUSE_CODE || (wasPausedRef.current && !currentlyPaused)) {
      // INBND is the internal "waiting" park, not a break reason — keep the dropdown clear.
      setPauseCodeInput('')
    }

    wasPausedRef.current = currentlyPaused
  }, [telephony.vicidialSession.pauseCode, telephony.vicidialSession.status])

  useEffect(() => {
    const isStandaloneDrawerView =
      new URLSearchParams(window.location.search).get(VICIDIAL_DRAWER_STANDALONE_QUERY_PARAM) ===
      '1'
    if (isStandaloneDrawerView) {
      const storedSnapshot = localStorage.getItem(STORAGE_NAMES.VICIDIAL_DRAWER_STANDALONE_STATE)
      if (storedSnapshot) {
        try {
          const snapshot = JSON.parse(storedSnapshot) as VicidialStandaloneDrawerSnapshot
          if (snapshot?.drawerState?.url) {
            setEmbeddedWebphoneUrl(snapshot.embeddedWebphoneUrl || '')
            setPauseCodeInput(snapshot.pauseCodeInput || '')
            setManualPhoneNumber(snapshot.manualPhoneNumber || '')
            setManualLeadId(snapshot.manualLeadId ?? null)
            setManualAutoDialPending(false)
            setDrawerState(snapshot.drawerState)
            setShow(true)
            setCollapsed(false)
          }
        } catch (error) {
          console.error('Unable to restore the standalone VICIdial drawer view', error)
        }
      }
    }

    const handleOpen = (event: Event) => {
      const customEvent = event as CustomEvent<TelephonyDrawerOpenDetail>
      if (!customEvent.detail?.url) return

      const payload = {...customEvent.detail}
      if (
        typeof window !== 'undefined' &&
        window.location.protocol === 'https:' &&
        payload.url.startsWith('http://')
      ) {
        payload.url = payload.url.replace(/^http:\/\//i, 'https://')
      }

      setEmbeddedWebphoneUrl('')
      setDrawerState(payload)
      setShow(true)
      setCollapsed(false)
    }

    const handleUpdate = (event: Event) => {
      const customEvent = event as CustomEvent<{diagnostics?: TelephonyDrawerDiagnostics}>
      if (!customEvent.detail?.diagnostics) return

      setDrawerState((current) => ({
        ...current,
        diagnostics: {
          ...(current.diagnostics || {}),
          ...customEvent.detail.diagnostics,
        },
      }))
    }

    const handleClose = () => {
      setEmbeddedWebphoneUrl('')
      setCollapsed(false)
      setShow(false)
    }

    const handleToggle = () => {
      if (!show) {
        if (drawerState.url) {
          setShow(true)
          setCollapsed(false)
        }
        return
      }

      setCollapsed((current) => !current)
    }

    const handleMessage = (event: MessageEvent) => {
      const data = event.data as {type?: string; webphoneUrl?: string} | undefined
      if (!data || data.type !== 'POLICYTOOLS_VICIDIAL_WEBPHONE_READY' || !data.webphoneUrl) {
        return
      }

      setEmbeddedWebphoneUrl(data.webphoneUrl)
      reportWebphoneBrowser() // this browser is hosting the webphone
      setDrawerState((current) => ({
        ...current,
        width: '460px',
        displayMode: 'webphone',
        diagnostics: {
          ...(current.diagnostics || {}),
          message: 'PolicyDial connected. Loading embedded webphone...',
        },
      }))
    }

    const handlePrepareDial = (event: Event) => {
      const customEvent = event as CustomEvent<TelephonyVicidialPrepareDialDetail>
      const phone = String(customEvent.detail?.phone || '').trim()
      if (!phone) return

      if (!telephony.vicidialSession.activeSession) {
        toast.error(intl.formatMessage(M.errConnectFirst))
        return
      }

      setManualPhoneNumber(phone)
      setManualLeadId(customEvent.detail?.leadId ?? null)
      setManualAutoDialPending(customEvent.detail?.autoDial !== false)
      setShow(true)
      setCollapsed(false)
    }

    window.addEventListener(TELEPHONY_DRAWER_OPEN_EVENT, handleOpen as EventListener)
    window.addEventListener(TELEPHONY_DRAWER_UPDATE_EVENT, handleUpdate as EventListener)
    window.addEventListener(TELEPHONY_DRAWER_CLOSE_EVENT, handleClose as EventListener)
    window.addEventListener(TELEPHONY_DRAWER_TOGGLE_EVENT, handleToggle as EventListener)
    window.addEventListener(
      TELEPHONY_VICIDIAL_PREPARE_DIAL_EVENT,
      handlePrepareDial as EventListener
    )
    window.addEventListener('message', handleMessage)

    const channel = new BroadcastChannel('policytools_vicidial_channel')
    channel.onmessage = (event) => {
      if (event.data === 'CLOSE_WEBPHONE') {
        closeTrackedExternalWindow()
        setEmbeddedWebphoneUrl('')
        setCollapsed(false)
        setShow(false)
      }
    }

    return () => {
      window.removeEventListener(TELEPHONY_DRAWER_OPEN_EVENT, handleOpen as EventListener)
      window.removeEventListener(TELEPHONY_DRAWER_UPDATE_EVENT, handleUpdate as EventListener)
      window.removeEventListener(TELEPHONY_DRAWER_CLOSE_EVENT, handleClose as EventListener)
      window.removeEventListener(TELEPHONY_DRAWER_TOGGLE_EVENT, handleToggle as EventListener)
      window.removeEventListener(
        TELEPHONY_VICIDIAL_PREPARE_DIAL_EVENT,
        handlePrepareDial as EventListener
      )
      window.removeEventListener('message', handleMessage)
      channel.close()
    }
  }, [drawerState.provider, drawerState.url, show, telephony])

  useEffect(() => {
    publishDrawerState(show, show && !collapsed, drawerState.provider)
  }, [collapsed, drawerState.provider, publishDrawerState, show])

  useEffect(() => {
    const handleRequest = () => {
      publishDrawerState(show, show && !collapsed, drawerState.provider)
    }
    window.addEventListener(TELEPHONY_DRAWER_STATE_REQUEST_EVENT, handleRequest)
    return () => {
      window.removeEventListener(TELEPHONY_DRAWER_STATE_REQUEST_EVENT, handleRequest)
    }
  }, [collapsed, drawerState.provider, publishDrawerState, show])

  useEffect(() => {
    if (
      !manualAutoDialPending ||
      !manualPhoneNumber.trim() ||
      !telephony.vicidialSession.activeSession ||
      runningAction === 'dial'
    ) {
      return
    }

    setManualAutoDialPending(false)
    void handleManualCall()
  }, [
    handleManualCall,
    manualAutoDialPending,
    manualPhoneNumber,
    runningAction,
    telephony.vicidialSession.activeSession,
  ])

  const activeCallPhoneNumber = String(
    telephony.vicidialSession.phoneNumber || drawerState.diagnostics?.phone_number || ''
  ).trim()
  const activeCallVendorLeadCode = String(
    telephony.vicidialSession.vendorLeadCode || drawerState.diagnostics?.vendor_lead_code || ''
  ).trim()
  const activeCallLeadId = String(telephony.vicidialSession.leadId || '').trim()
  const activeCallSubStatus = String(
    telephony.vicidialSession.realTimeSubStatus ||
      drawerState.diagnostics?.real_time_sub_status ||
      ''
  )
    .trim()
    .toUpperCase()
  const effectiveVicidialStatus = useMemo(() => {
    const rawStatus = String(
      drawerState.diagnostics?.status || telephony.vicidialSession.status || ''
    )
      .trim()
      .toUpperCase()
    const activeCallId = String(
      telephony.vicidialSession.callId || drawerState.diagnostics?.call_id || ''
    ).trim()
    if (isDispositionPendingState(rawStatus, activeCallSubStatus)) {
      return 'DEAD'
    }
    if (
      rawStatus === 'QUEUE' &&
      (hasMeaningfulVicidialValue(activeCallId) ||
        activeCallSubStatus.includes('INCALL') ||
        activeCallSubStatus.includes('LIVE') ||
        activeCallSubStatus.includes('RING'))
    ) {
      return 'INCALL'
    }
    return rawStatus
  }, [
    activeCallSubStatus,
    drawerState.diagnostics?.call_id,
    drawerState.diagnostics?.status,
    telephony.vicidialSession.callId,
    telephony.vicidialSession.status,
  ])

  const isPausedState = effectiveVicidialStatus === 'PAUSED'
  const isReadyState =
    Boolean(telephony.vicidialSession.activeSession || drawerState.diagnostics?.session_id) &&
    !isPausedState &&
    effectiveVicidialStatus !== 'DEAD'
  const isPauseActionRunning = runningAction === 'pause'
  const isResumeActionRunning = runningAction === 'resume'
  // A logged-in agent who is PAUSED must always be able to resume ("DIAL NEXT
  // NUMBER"), even while the session marker is briefly re-connecting or
  // active_session momentarily reads false right after login (VICIdial reports
  // status=LOGIN for a few seconds before PAUSED, and PolicyTools agents have no
  // native screen to transition them). Previously requiring !connecting &&
  // activeSession left the button greyed out at login, stranding the agent.
  // Only hard-block during an explicit disconnect or an in-flight pause/resume.
  const canTogglePauseState =
    !disconnecting &&
    !runningAction &&
    (isPausedState || Boolean(telephony.vicidialSession.activeSession))
  // Forced pause reasons: the agent MUST pick one before pausing. Codes must
  // match vicidial_pause_codes for the campaign (BATH/LUNCH/SHORT/LONG).
  const pauseCodeOptions: Array<{code: string; label: string}> = [
    {code: 'BATH', label: intl.formatMessage(M.pauseBath)},
    {code: 'LUNCH', label: intl.formatMessage(M.pauseLunch)},
    {code: 'SHORT', label: intl.formatMessage(M.pauseShort)},
    {code: 'LONG', label: intl.formatMessage(M.pauseLong)},
  ]
  // Resuming ("DIAL NEXT NUMBER") needs no code; only pausing requires one.
  const pauseCodeRequiredButMissing = !isPausedState && !pauseCodeInput.trim()
  const isInboundWaitPause =
    isPausedState &&
    String(drawerState.diagnostics?.pause_code || telephony.vicidialSession.pauseCode || '')
      .trim()
      .toUpperCase() === MANUAL_INBOUND_PAUSE_CODE
  const pauseStatusLabel = intl.formatMessage(
    isPausedState
      ? isInboundWaitPause
        ? M.waitingForCalls
        : M.youArePaused
      : isReadyState
      ? M.youAreActive
      : M.sessionNotReady
  )
  const pauseActionLabel = intl.formatMessage(
    isPausedState ? M.dialNextNumber : M.activePause
  )
  const pauseActionBusyLabel = intl.formatMessage(isPausedState ? M.goingActive : M.pausing)
  const pauseActionVariant = isPausedState ? 'success' : 'warning'

  const hasStructuredDiagnostics = Boolean(
    drawerState.diagnostics?.agent_user ||
      drawerState.diagnostics?.status ||
      drawerState.diagnostics?.campaign_id ||
      drawerState.diagnostics?.pause_code ||
      drawerState.diagnostics?.session_id ||
      drawerState.diagnostics?.phone_number
  )
  const dispositionPending = isDispositionPendingState(effectiveVicidialStatus, activeCallSubStatus)
  const hasLiveCallContext =
    !dispositionPending &&
    (hasMeaningfulVicidialValue(
      telephony.vicidialSession.callId || drawerState.diagnostics?.call_id
    ) ||
      hasMeaningfulVicidialValue(activeCallLeadId) ||
      hasMeaningfulVicidialValue(activeCallPhoneNumber) ||
      hasMeaningfulVicidialValue(activeCallVendorLeadCode) ||
      ['INCALL', 'LIVE', 'QUEUE', 'RING'].some((marker) =>
        effectiveVicidialStatus.includes(marker)
      ) ||
      ['INCALL', 'LIVE', 'QUEUE', 'RING'].some((marker) => activeCallSubStatus.includes(marker)))
  const activeCallBannerTitle =
    effectiveVicidialStatus === 'INCALL' ||
    activeCallSubStatus.includes('INCALL') ||
    activeCallSubStatus.includes('LIVE')
      ? 'PolicyDial call active'
      : 'Incoming PolicyDial call'
  const activeConversationInProgress =
    effectiveVicidialStatus === 'INCALL' ||
    activeCallSubStatus.includes('INCALL') ||
    activeCallSubStatus.includes('LIVE')
  const selectedPhoneNumber = manualPhoneNumber.trim()
  const currentAgentUser = String(
    drawerState.diagnostics?.agent_user || telephony.vicidialSession.agentUser || 'Not assigned'
  ).trim()
  const currentCampaignId = String(
    drawerState.diagnostics?.campaign_id || telephony.vicidialSession.campaignId || 'Not assigned'
  ).trim()
  const currentSessionId = String(
    drawerState.diagnostics?.session_id || telephony.vicidialSession.sessionId || 'Pending'
  ).trim()
  const currentPauseCode = String(
    drawerState.diagnostics?.pause_code || telephony.vicidialSession.pauseCode || ''
  )
    .trim()
    .toUpperCase()
  // Manual campaign: a call is "ringing" only when the agent is parked in the INBND
  // waiting pause (available) with a call queued — NOT while on a real break, and they
  // must press Answer (resume) to take it.
  const isManualCampaign = currentCampaignId === MANUAL_CAMPAIGN_ID
  // Load blind-transfer destinations (colleagues + queues, with live availability)
  // once the agent is on a live call in a manual campaign, where transfer applies.
  // Hidden/skipped on autodial campaigns.
  const transferLoadableSession = canOperateVicidial && isManualCampaign && telephony.vicidialSession.activeSession
  useEffect(() => {
    if (transferLoadableSession) {
      void telephony.loadTransferTargets()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transferLoadableSession])
  const transferAgentOptions = telephony.transferTargets?.agents ?? []
  const transferQueueOptions = telephony.transferTargets?.in_groups ?? []
  const isWaitingInbound = isManualCampaign && isPausedState && currentPauseCode === MANUAL_INBOUND_PAUSE_CODE
  const waitingInboundCount = telephony.vicidialSession.pendingInboundCount
  const isRingingInbound =
    isWaitingInbound && waitingInboundCount > 0 && !activeConversationInProgress
  const currentPhoneNumber = String(
    telephony.vicidialSession.phoneNumber || drawerState.diagnostics?.phone_number || ''
  ).trim()
  // The agent's own live connection quality (RTP), so they can see how good
  // their connection is. Null when there's no active media on their channel.
  const connectionAudio = telephony.vicidialSession.audio
  const connectionLossPct = connectionAudio
    ? Math.max(connectionAudio.recv_loss_pct, connectionAudio.send_loss_pct)
    : 0
  // Cifras y unidades (%, ms): es formato, no texto traducible.
  const connectionAudioStats = connectionAudio
    ? `${connectionLossPct}${PCT}${DOT_SEP}${connectionAudio.jitter_ms}${MS}`
    : ''
  if (connectionAudio) {
    noMediaSinceRef.current = null
  } else if (noMediaSinceRef.current === null) {
    noMediaSinceRef.current = Date.now()
  }
  // Only warn after 60s of sustained no-media (the poll re-renders every few
  // seconds, so this flips on once the threshold passes).
  const showNoAudioConnection =
    !connectionAudio &&
    noMediaSinceRef.current !== null &&
    Date.now() - noMediaSinceRef.current >= 60000
  const callContextName = [
    String(telephony.vicidialSession.firstName || '').trim(),
    String(telephony.vicidialSession.lastName || '').trim(),
  ]
    .filter((value) => hasMeaningfulVicidialValue(value))
    .join(' ')
  const drawerTone = dispositionPending
    ? 'wrapup'
    : hasLiveCallContext
    ? 'live'
    : isPausedState
    ? 'paused'
    : isReadyState
    ? 'active'
    : 'idle'
  const canOpenDetachedWindow = Boolean(drawerState.url)
  const showUndockButton = canOpenDetachedWindow && drawerState.provider !== 'VICIDIAL'
  const policyToolsLookupPhone = useMemo(() => {
    const preferredPhone = hasLiveCallContext
      ? activeCallPhoneNumber
      : selectedPhoneNumber || drawerState.diagnostics?.phone_number || ''
    const normalizedPhone = normalizePhoneDigits(preferredPhone)
    return normalizedPhone.length >= 10 ? normalizedPhone : ''
  }, [
    activeCallPhoneNumber,
    drawerState.diagnostics?.phone_number,
    hasLiveCallContext,
    selectedPhoneNumber,
  ])
  const showPreparedDialInline =
    canOperateVicidial && !hasLiveCallContext && !dispositionPending && Boolean(selectedPhoneNumber)

  useEffect(() => {
    if (!show) {
      policyToolsLookupPhoneRef.current = ''
      setPolicyToolsContact(null)
      return
    }
    // Don't drop the contact when policyToolsLookupPhone goes blank for a tick
    // (status polls during live <-> idle transitions briefly return no phone).
    // The dedicated call-lifecycle effect below clears it on actual hangup.
    if (!policyToolsLookupPhone) return
    if (policyToolsLookupPhoneRef.current === policyToolsLookupPhone) return

    policyToolsLookupPhoneRef.current = policyToolsLookupPhone
    setPolicyToolsContact(null)
    let ignore = false

    lookupPolicyToolsContact({
      variables: {name: policyToolsLookupPhone},
      fetchPolicy: 'no-cache',
    })
      .then(({data}) => {
        if (ignore || policyToolsLookupPhoneRef.current !== policyToolsLookupPhone) return
        const contacts = (data?.allContactsPreview?.edges || [])
          .map((edge) => edge?.node)
          .filter(Boolean) as NodeContact[]
        setPolicyToolsContact(pickBestPolicyToolsContact(contacts, policyToolsLookupPhone))
      })
      .catch((error) => {
        if (ignore || policyToolsLookupPhoneRef.current !== policyToolsLookupPhone) return
        console.error('Error looking up PolicyTools contact for telephony drawer', error)
        setPolicyToolsContact(null)
      })

    return () => {
      ignore = true
    }
  }, [lookupPolicyToolsContact, policyToolsLookupPhone, show])

  const policyToolsLookupLeadId = useMemo(() => {
    const candidates = [activeCallVendorLeadCode, manualLeadId]
    for (const candidate of candidates) {
      const value = String(candidate || '').trim()
      if (hasMeaningfulVicidialValue(value)) return value
    }
    return ''
  }, [activeCallVendorLeadCode, manualLeadId])

  useEffect(() => {
    if (!show) {
      policyToolsLookupLeadIdRef.current = ''
      setPolicyToolsLead(null)
      return
    }
    // Same logic as the contact lookup above: keep the previous lead on screen
    // while the lookup id flickers empty. Call-lifecycle effect handles hangup.
    if (!policyToolsLookupLeadId) return
    if (policyToolsLookupLeadIdRef.current === policyToolsLookupLeadId) return

    policyToolsLookupLeadIdRef.current = policyToolsLookupLeadId
    setPolicyToolsLead(null)
    let ignore = false

    lookupPolicyToolsLead({
      variables: {id: policyToolsLookupLeadId},
      fetchPolicy: 'no-cache',
    })
      .then(({data}) => {
        if (ignore || policyToolsLookupLeadIdRef.current !== policyToolsLookupLeadId) return
        const lead = (data as {leadById?: typeof policyToolsLead})?.leadById || null
        setPolicyToolsLead(lead || null)
      })
      .catch((error) => {
        if (ignore || policyToolsLookupLeadIdRef.current !== policyToolsLookupLeadId) return
        console.error('Error looking up PolicyTools lead for telephony drawer', error)
        setPolicyToolsLead(null)
      })

    return () => {
      ignore = true
    }
  }, [lookupPolicyToolsLead, policyToolsLookupLeadId, show])

  const policyToolsLeadName = useMemo(() => {
    if (!policyToolsLead) return ''
    const fromContact = [
      String(policyToolsLead.contact?.firstName || '').trim(),
      String(policyToolsLead.contact?.lastName || '').trim(),
    ]
      .filter(Boolean)
      .join(' ')
      .trim()
    return fromContact || String(policyToolsLead.name || '').trim()
  }, [policyToolsLead])

  const policyToolsPolicyRows = useMemo(() => {
    if (!policyToolsContact) return []
    const seen = new Set<string>()
    const policies: MemberPolicySummary[] = []

    if (policyToolsContact.memberPolicy?.policy?.id) {
      seen.add(policyToolsContact.memberPolicy.policy.id)
      policies.push(policyToolsContact.memberPolicy)
    }

    for (const edge of policyToolsContact.members?.edges || []) {
      const memberPolicy = edge?.node
      const policyId = memberPolicy?.policy?.id
      if (!memberPolicy || !policyId || seen.has(policyId)) continue
      seen.add(policyId)
      policies.push(memberPolicy)
    }

    return policies
  }, [policyToolsContact])

  const policyToolsContactName =
    String(policyToolsContact?.fullName || '').trim() ||
    [
      String(policyToolsContact?.firstName || '').trim(),
      String(policyToolsContact?.lastName || '').trim(),
    ]
      .filter(Boolean)
      .join(' ')
  const policyToolsLocation = formatPolicyToolsLocation(policyToolsContact)
  const hasPolicyToolsContact = Boolean(
    policyToolsContact && phonesLikelyMatch(policyToolsContact.phone, policyToolsLookupPhone)
  )
  // Enriched contact fields for the Live Conversation grid (PolicyTools-only).
  const policyToolsPrimaryPolicy =
    policyToolsContact?.memberPolicy?.policy ||
    policyToolsPolicyRows.find((row) => row.policy)?.policy ||
    null
  const policyToolsAge = computePolicyToolsAge(policyToolsContact?.dateBirth)
  const policyToolsAgent = formatPolicyToolsPersonName(policyToolsPrimaryPolicy?.user)
  const policyToolsAssignedTo = formatPolicyToolsPersonName(policyToolsPrimaryPolicy?.assignedTo)
  const policyToolsHasActivePolicy = policyToolsPolicyRows.some(
    (row) => row.policy && !row.policy.isArchived
  )
  // Group the contact's policies by status with a count (avoids repeating the name).
  const policyToolsPolicyGroups = useMemo(() => {
    const groups = new Map<string, number>()
    for (const row of policyToolsPolicyRows) {
      const label = String(row.policy?.status || row.policy?.paymentStatus || '')
        .trim()
        .toUpperCase()
      if (!label) continue
      groups.set(label, (groups.get(label) || 0) + 1)
    }
    return Array.from(groups.entries()).map(([status, count]) => ({status, count}))
  }, [policyToolsPolicyRows])
  // When the call comes from a VICIdial auto-dial campaign, the lead's
  // first_name + last_name were uploaded into vicidial_list and travel down
  // through agent_status.lead_info. Prefer that authoritative source over the
  // PolicyTools-side phone match (which can collide with unrelated contacts
  // that happen to share the same number).
  const preferredCallContextName =
    callContextName ||
    (hasPolicyToolsContact && policyToolsContactName) ||
    policyToolsLeadName
  const activeCallElapsedLabel =
    liveCallStartedAt && activeConversationInProgress
      ? formatElapsedClock(nowTick - liveCallStartedAt)
      : ''
  const preparedDialHeadline = hasPolicyToolsContact
    ? policyToolsContactName || intl.formatMessage(M.policyToolsContactReady)
    : policyToolsLeadName ||
      intl.formatMessage(runningAction === 'dial' ? M.callingThrough : M.numberReady)
  const preparedDialPolicyLabels = policyToolsPolicyRows
    .map((memberPolicy) => buildPolicyToolsPolicyLabel(memberPolicy))
    .filter(Boolean)

  useEffect(() => {
    if (!selectedPhoneNumber) {
      preparedDialLifecycleSeenRef.current = false
      return
    }

    if (hasLiveCallContext || dispositionPending) {
      preparedDialLifecycleSeenRef.current = true
      return
    }

    if (!preparedDialLifecycleSeenRef.current) return

    preparedDialLifecycleSeenRef.current = false
    policyToolsLookupPhoneRef.current = ''
    setPolicyToolsContact(null)
    setManualPhoneNumber('')
    setManualLeadId(null)
  }, [dispositionPending, hasLiveCallContext, selectedPhoneNumber])

  // Clear the PolicyTools contact / lead panel when a call actually ends
  // (live + wrap-up both turn false), regardless of whether the user had
  // pre-selected a phone for the next dial. Replaces the previous behavior
  // of clearing on empty lookup phone, which made the info flicker out
  // during normal live <-> idle status polls.
  useEffect(() => {
    const inCall = hasLiveCallContext || dispositionPending
    if (inCall) {
      wasInCallRef.current = true
      return
    }
    if (!wasInCallRef.current) return
    wasInCallRef.current = false
    policyToolsLookupPhoneRef.current = ''
    policyToolsLookupLeadIdRef.current = ''
    setPolicyToolsContact(null)
    setPolicyToolsLead(null)
  }, [hasLiveCallContext, dispositionPending])

  // Single-owner guard: agents keep many PolicyTools tabs open; only one may run the
  // webphone or the SIP registrations fight (stolen contact / split audio). Other tabs
  // show a "running in another tab" panel instead of a second webphone.
  const webphoneActive = show && Boolean(drawerState.url)
  const webphoneInCall = Boolean(liveCallStartedAt)
  const webphoneOwnership = useWebphoneOwnership({active: webphoneActive, inCall: webphoneInCall})
  const webphoneBlockedByOtherTab = webphoneOwnership.ownerElsewhere && !webphoneOwnership.isOwner

  if (!show) return null

  return (
    <div
      role='dialog'
      aria-hidden={collapsed}
      className={`telephony-drawer telephony-drawer--${drawerTone}`}
      style={{
        width: drawerState.width || '460px',
        maxWidth: '100%',
        transform: collapsed ? 'translateX(100%)' : 'translateX(0)',
        opacity: collapsed ? 0 : 1,
        pointerEvents: collapsed ? 'none' : 'auto',
        visibility: collapsed ? 'hidden' : 'visible',
      }}
    >
      <div className='telephony-drawer__header'>
        <div className='telephony-drawer__header-actions'>
          <Button
            variant='light'
            size='sm'
            onClick={() => setShowAudioCheck(true)}
            className='telephony-drawer__ghost-button'
            title={intl.formatMessage(M.audioCheckTooltip)}
          >
            <i className='bi bi-gear me-2' />
            {intl.formatMessage(M.audio)}
          </Button>
          {showUndockButton && (
            <Button
              variant='light'
              size='sm'
              onClick={openInNewTab}
              disabled={disconnecting}
              className='telephony-drawer__ghost-button'
            >
              <i className='bi bi-box-arrow-up-right me-2' />
              {intl.formatMessage(M.undock)}
            </Button>
          )}
          {drawerState.provider === 'VICIDIAL' && canOperateVicidial && (
            <Button
              variant='light'
              size='sm'
              onClick={() => void handleVicidialLogout()}
              disabled={disconnecting}
              className='telephony-drawer__ghost-button telephony-drawer__ghost-button--danger'
            >
              <i className='bi bi-box-arrow-right me-2' />
              {intl.formatMessage(disconnecting ? M.loggingOut : M.logout)}
            </Button>
          )}
          <CloseButton
            onClick={() => setCollapsed(true)}
            aria-label={intl.formatMessage(M.close)}
            className='telephony-drawer__close'
          />
        </div>
      </div>
      <div className='telephony-drawer__body'>
        {shouldForceExternalOpen && (
          <Alert variant='warning' className='telephony-drawer__alert'>
            <div className='fw-semibold mb-1'>{intl.formatMessage(M.secureTabRequired)}</div>
            <div className='small'>
              <FormattedMessage {...M.secureTabBody} values={{code: (c) => <code>{c}</code>}} />
            </div>
            <div className='mt-3'>
              <Button
                variant='warning'
                size='sm'
                onClick={openInNewTab}
                disabled={!canOpenDetachedWindow || disconnecting}
              >
                {intl.formatMessage(M.openSecureWebphone)}
              </Button>
            </div>
          </Alert>
        )}
        {(drawerState.provider === 'VICIDIAL' || drawerState.diagnostics) && !isAgentScreenMode && (
          <section className='telephony-drawer__section telephony-drawer__section--hero'>
            <div className='telephony-drawer__hero-bar'>
              <div className='telephony-drawer__hero-copy'>
                <div className='telephony-drawer__section-kicker'>
                  {intl.formatMessage(M.sessionOverview)}
                </div>
                <div className='telephony-drawer__hero-status-row'>
                  <span
                    className={`telephony-drawer__status-pill telephony-drawer__status-pill--${drawerTone}`}
                    style={
                      isInboundWaitPause
                        ? {backgroundColor: '#dcfce7', color: '#15803d', borderColor: '#86efac'}
                        : undefined
                    }
                  >
                    {isInboundWaitPause
                      ? intl.formatMessage(M.waitingForCalls)
                      : effectiveVicidialStatus || intl.formatMessage(M.statusPending)}
                  </span>
                  <span className='telephony-drawer__hero-note'>
                    {isInboundWaitPause ? '' : pauseStatusLabel}
                  </span>
                </div>
              </div>
            </div>
            <div className='telephony-drawer__summary-grid telephony-drawer__summary-grid--compact'>
              <div className='telephony-drawer__summary-item'>
                <span className='telephony-drawer__summary-label'>
                  {intl.formatMessage(M.agent)}
                </span>
                <span className='telephony-drawer__summary-value'>{currentAgentUser}</span>
              </div>
              <div className='telephony-drawer__summary-item'>
                <span className='telephony-drawer__summary-label'>
                  {intl.formatMessage(M.campaign)}
                </span>
                <span className='telephony-drawer__summary-value'>{currentCampaignId}</span>
              </div>
              <div className='telephony-drawer__summary-item'>
                <span className='telephony-drawer__summary-label'>
                  {intl.formatMessage(M.session)}
                </span>
                <span className='telephony-drawer__summary-value telephony-drawer__summary-value--mono'>
                  {currentSessionId}
                </span>
              </div>
              {/* Caller ID picker + manual dial only make sense in the manual campaign;
                  autodial agents wait for the dialer to place calls and pick the CID, so
                  both are hidden there. */}
              {isManualCampaign && cidOptions.length > 0 && (
                <div className='telephony-drawer__summary-item'>
                  <span className='telephony-drawer__summary-label'>
                    {intl.formatMessage(M.callerId)}
                  </span>
                  <Form.Select
                    size='sm'
                    value={selectedCid}
                    onChange={(e) => setSelectedCid(e.target.value)}
                    title={intl.formatMessage(M.callerIdTooltip)}
                  >
                    {cidOptions.map((n) => {
                      const isGroup = n.routeMode === 'GROUP'
                      return (
                        <option key={n.e164} value={n.e164}>
                          {intl.formatMessage(isGroup ? M.inboundGroup : M.directAgent)}
                          {isGroup && n.assignedGroupName ? DOT_SEP + n.assignedGroupName : ''}
                          {DOT_SEP + n.e164}
                          {n.isOutboundCid ? intl.formatMessage(M.defaultSuffix) : ''}
                        </option>
                      )
                    })}
                  </Form.Select>
                </div>
              )}
            </div>
            <div className='telephony-drawer__chip-row telephony-drawer__chip-row--tight'>
              {connectionAudio ? (
                <Badge
                  bg='light'
                  text='dark'
                  className='border telephony-drawer__chip d-inline-flex align-items-center gap-2'
                  title={intl.formatMessage(M.connectionTooltip, {
                    codec: connectionAudio.codec || NOT_AVAILABLE,
                    loss: connectionLossPct,
                    jitter: connectionAudio.jitter_ms,
                  })}
                >
                  <SignalBars quality={connectionAudio.quality} />
                  <span className='text-capitalize'>
                    {connectionAudio.quality === 'idle'
                      ? intl.formatMessage(M.idle)
                      : connectionAudio.quality}
                  </span>
                  {connectionAudio.quality === 'idle' ? (
                    <span className='text-muted'>{intl.formatMessage(M.noActiveCall)}</span>
                  ) : (
                    <span className='text-muted'>{connectionAudioStats}</span>
                  )}
                </Badge>
              ) : showNoAudioConnection ? (
                <span
                  className='badge border telephony-drawer__chip telephony-drawer__chip--no-audio d-inline-flex align-items-center gap-2'
                  title={intl.formatMessage(M.noAudioConnectionTooltip)}
                >
                  <i className='bi bi-reception-0' />
                  {intl.formatMessage(M.noAudioConnection)}
                </span>
              ) : null}
              {currentPauseCode && !isWaitingInbound ? (
                <Badge bg='light' text='dark' className='border telephony-drawer__chip'>
                  {intl.formatMessage(M.pauseCodeChip, {code: currentPauseCode})}
                </Badge>
              ) : null}
              {telephony.vicidialSession.callsInQueueCount > 0 ? (
                <Badge bg='light' text='dark' className='border telephony-drawer__chip'>
                  {intl.formatMessage(M.queueChip, {
                    count: telephony.vicidialSession.callsInQueueCount,
                  })}
                </Badge>
              ) : null}
              {hasMeaningfulVicidialValue(currentPhoneNumber) &&
              !showPreparedDialInline &&
              !hasLiveCallContext ? (
                <Badge bg='light' text='dark' className='border telephony-drawer__chip'>
                  {intl.formatMessage(M.phoneChip, {value: currentPhoneNumber})}
                </Badge>
              ) : null}
            </div>
            {showPreparedDialInline ? (
              <div className='telephony-drawer__inline-context'>
                <div className='telephony-drawer__inline-context-head'>
                  <span className='telephony-drawer__summary-label'>
                    {intl.formatMessage(M.dialTarget)}
                  </span>
                  {hasPolicyToolsContact ? (
                    <Badge bg='light' text='dark' className='border telephony-drawer__chip'>
                      {intl.formatMessage(M.policyToolsContact)}
                    </Badge>
                  ) : null}
                </div>
                <div className='telephony-drawer__inline-context-title'>{preparedDialHeadline}</div>
                <div className='telephony-drawer__chip-row telephony-drawer__chip-row--tight'>
                  <Badge bg='light' text='dark' className='border telephony-drawer__chip'>
                    {intl.formatMessage(M.phoneChip, {value: selectedPhoneNumber})}
                  </Badge>
                  {hasMeaningfulVicidialValue(policyToolsContact?.email) ? (
                    <Badge bg='light' text='dark' className='border telephony-drawer__chip'>
                      {intl.formatMessage(M.emailChip, {value: policyToolsContact?.email})}
                    </Badge>
                  ) : null}
                  {hasMeaningfulVicidialValue(policyToolsLocation) ? (
                    <Badge bg='light' text='dark' className='border telephony-drawer__chip'>
                      {intl.formatMessage(M.locationChip, {value: policyToolsLocation})}
                    </Badge>
                  ) : null}
                  {hasMeaningfulVicidialValue(policyToolsContact?.lead?.name) ? (
                    <Badge bg='light' text='dark' className='border telephony-drawer__chip'>
                      {intl.formatMessage(M.leadChip, {value: policyToolsContact?.lead?.name})}
                    </Badge>
                  ) : null}
                  {!hasPolicyToolsContact && policyToolsLeadName ? (
                    <Badge bg='light' text='dark' className='border telephony-drawer__chip'>
                      {intl.formatMessage(M.leadChip, {value: policyToolsLeadName})}
                    </Badge>
                  ) : !hasPolicyToolsContact && manualLeadId ? (
                    <Badge bg='light' text='dark' className='border telephony-drawer__chip'>
                      {intl.formatMessage(M.leadIdChip, {value: manualLeadId})}
                    </Badge>
                  ) : null}
                  {preparedDialPolicyLabels[0] ? (
                    <Badge bg='light' text='dark' className='border telephony-drawer__chip'>
                      {preparedDialPolicyLabels[0]}
                    </Badge>
                  ) : null}
                  {preparedDialPolicyLabels.length > 1 ? (
                    <Badge bg='light' text='dark' className='border telephony-drawer__chip'>
                      {intl.formatMessage(M.morePolicies, {
                        count: preparedDialPolicyLabels.length - 1,
                      })}
                    </Badge>
                  ) : null}
                </div>
              </div>
            ) : null}
            {!hasStructuredDiagnostics && drawerState.diagnostics?.raw_response ? (
              <details className='telephony-drawer__details'>
                <summary>{intl.formatMessage(M.advancedDiagnostics)}</summary>
                <pre className='telephony-drawer__diagnostics-pre'>
                  {drawerState.diagnostics.raw_response}
                </pre>
              </details>
            ) : null}
          </section>
        )}
        {canOperateVicidial && hasLiveCallContext && (
          <section className='telephony-drawer__section telephony-drawer__section--call'>
            <div className='telephony-drawer__section-head'>
              <div>
                <div className='telephony-drawer__section-kicker'>
                  {intl.formatMessage(M.liveConversation)}
                </div>
                <div className='telephony-drawer__section-title'>{activeCallBannerTitle}</div>
                {activeCallElapsedLabel ? (
                  <div className='telephony-drawer__call-timer' aria-live='polite'>
                    <span className='telephony-drawer__call-timer-label'>
                      {intl.formatMessage(M.onCall)}
                    </span>
                    <span className='telephony-drawer__call-timer-value'>
                      {activeCallElapsedLabel}
                    </span>
                  </div>
                ) : null}
              </div>
              <Button
                variant='danger'
                size='sm'
                onClick={handleHangupCurrentCall}
                disabled={
                  disconnecting ||
                  telephony.vicidialSession.connecting ||
                  runningAction === 'hangup'
                }
                className='telephony-drawer__danger-button'
              >
                {intl.formatMessage(runningAction === 'hangup' ? M.hangingUp : M.hangUp)}
              </Button>
            </div>
            {preferredCallContextName || hasPolicyToolsContact ? (
              <div className='telephony-drawer__lead-row'>
                <span className='telephony-drawer__lead-name'>
                  {preferredCallContextName ||
                    policyToolsContactName ||
                    intl.formatMessage(M.caller)}
                </span>
                {hasPolicyToolsContact ? (
                  <span className='telephony-drawer__source-tag telephony-drawer__source-tag--contact'>
                    {intl.formatMessage(M.policyToolsContact)}
                  </span>
                ) : (
                  <span className='telephony-drawer__source-tag telephony-drawer__source-tag--lead'>
                    {intl.formatMessage(M.vicidialLead)}
                  </span>
                )}
              </div>
            ) : null}
            <div className='telephony-drawer__info-grid'>
              {hasMeaningfulVicidialValue(activeCallPhoneNumber) ? (
                <div className='telephony-drawer__info-field'>
                  <span className='telephony-drawer__info-label'>
                    {intl.formatMessage(M.phone)}
                  </span>
                  <span className='telephony-drawer__info-value telephony-drawer__info-value--mono'>
                    {activeCallPhoneNumber}
                  </span>
                </div>
              ) : null}
              {hasPolicyToolsContact ? (
                <>
                  {policyToolsAge !== null ? (
                    <div className='telephony-drawer__info-field'>
                      <span className='telephony-drawer__info-label'>
                    {intl.formatMessage(M.age)}
                  </span>
                      <span className='telephony-drawer__info-value'>{policyToolsAge}</span>
                    </div>
                  ) : null}
                  <div className='telephony-drawer__info-field'>
                    <span className='telephony-drawer__info-label'>
                    {intl.formatMessage(M.activePolicy)}
                  </span>
                    <span
                      className={`telephony-drawer__info-value${
                        policyToolsHasActivePolicy ? ' telephony-drawer__info-value--ok' : ''
                      }`}
                    >
                      {intl.formatMessage(policyToolsHasActivePolicy ? M.yes : M.no)}
                    </span>
                  </div>
                  {hasMeaningfulVicidialValue(policyToolsLocation) ? (
                    <div className='telephony-drawer__info-field'>
                      <span className='telephony-drawer__info-label'>
                    {intl.formatMessage(M.location)}
                  </span>
                      <span className='telephony-drawer__info-value'>{policyToolsLocation}</span>
                    </div>
                  ) : null}
                  {policyToolsAgent ? (
                    <div className='telephony-drawer__info-field'>
                      <span className='telephony-drawer__info-label'>
                    {intl.formatMessage(M.agent)}
                  </span>
                      <span className='telephony-drawer__info-value'>{policyToolsAgent}</span>
                    </div>
                  ) : null}
                  {policyToolsAssignedTo ? (
                    <div className='telephony-drawer__info-field'>
                      <span className='telephony-drawer__info-label'>
                    {intl.formatMessage(M.assignedTo)}
                  </span>
                      <span className='telephony-drawer__info-value'>{policyToolsAssignedTo}</span>
                    </div>
                  ) : null}
                </>
              ) : (
                <>
                  {hasMeaningfulVicidialValue(activeCallLeadId) ? (
                    <div className='telephony-drawer__info-field'>
                      <span className='telephony-drawer__info-label'>
                    {intl.formatMessage(M.leadId)}
                  </span>
                      <span className='telephony-drawer__info-value telephony-drawer__info-value--mono'>
                        {activeCallLeadId}
                      </span>
                    </div>
                  ) : null}
                  {hasMeaningfulVicidialValue(activeCallVendorLeadCode) ? (
                    <div className='telephony-drawer__info-field'>
                      <span className='telephony-drawer__info-label'>
                    {intl.formatMessage(M.vendorId)}
                  </span>
                      <span className='telephony-drawer__info-value telephony-drawer__info-value--mono'>
                        {activeCallVendorLeadCode}
                      </span>
                    </div>
                  ) : null}
                </>
              )}
              {hasMeaningfulVicidialValue(
                telephony.vicidialSession.email || policyToolsContact?.email
              ) ? (
                <div className='telephony-drawer__info-field telephony-drawer__info-field--full'>
                  <span className='telephony-drawer__info-label'>
                    {intl.formatMessage(M.email)}
                  </span>
                  <span className='telephony-drawer__info-value'>
                    {telephony.vicidialSession.email ||
                      (hasPolicyToolsContact ? policyToolsContact?.email : '')}
                  </span>
                </div>
              ) : null}
            </div>
            {hasMeaningfulVicidialValue(telephony.vicidialSession.comments) ? (
              <div
                className='border rounded p-2 mt-2 small'
                style={{ background: '#fff8e1', borderColor: '#ffd54f', whiteSpace: 'pre-wrap' }}
              >
                <span className='fw-bold me-1'>
                  <i className='bi bi-sticky me-1' />
                  {intl.formatMessage(M.notesLabel)}
                </span>
                {telephony.vicidialSession.comments}
              </div>
            ) : null}
            {hasPolicyToolsContact && policyToolsPolicyGroups.length > 0 ? (
              <div className='telephony-drawer__policies'>
                <span className='telephony-drawer__policies-label'>
                  {intl.formatMessage(M.policiesCount, {
                    count: policyToolsPolicyRows.length,
                  })}
                </span>
                <div className='telephony-drawer__policies-chips'>
                  {policyToolsPolicyGroups.map((group) => (
                    <span
                      key={group.status}
                      className={`telephony-drawer__policy-chip telephony-drawer__policy-chip--${policyToolsStatusTone(
                        group.status
                      )}`}
                    >
                      <span className='telephony-drawer__policy-count'>{group.count}</span>
                      {group.status}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
            {!hasPolicyToolsContact ? (
              <div className='telephony-drawer__not-found'>
                <FormattedMessage
                  {...M.notInPolicyTools}
                  values={{b: (c) => <strong>{c}</strong>}}
                />
              </div>
            ) : null}
          </section>
        )}
        {canOperateVicidial &&
          dispositionPending &&
          wrapUpStartedAt &&
          (() => {
            const elapsedSec = Math.max(0, Math.floor((nowTick - wrapUpStartedAt) / 1000))
            const minutes = Math.floor(elapsedSec / 60)
            const seconds = elapsedSec % 60
            // Reloj mm:ss: es formato, no texto traducible.
            const wrapUpClock = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
            return (
              <div className='telephony-drawer__wrapup-banner'>
                <i className='bi bi-hourglass-split text-warning' />
                <span className='fw-semibold small'>{intl.formatMessage(M.wrapUp)}</span>
                <span className='fw-bold'>{wrapUpClock}</span>
                <span className='telephony-drawer__wrapup-note'>
                  {intl.formatMessage(M.wrapUpNote)}
                </span>
              </div>
            )
          })()}
        {isRingingInbound && (
          <section className='telephony-drawer__ringing'>
            <div className='telephony-drawer__ringing-pulse'>
              <i className='bi bi-telephone-inbound-fill' />
            </div>
            <div className='telephony-drawer__ringing-text'>
              <div className='telephony-drawer__ringing-title'>
                {formatCallerId(telephony.vicidialSession.pendingInboundCaller) ||
                  intl.formatMessage(M.incomingCall)}
              </div>
              <div className='telephony-drawer__ringing-sub'>
                {waitingInboundCount > 1
                  ? intl.formatMessage(M.callsWaiting, {count: waitingInboundCount})
                  : intl.formatMessage(
                      telephony.vicidialSession.pendingInboundCaller
                        ? M.incomingPressAnswer
                        : M.pressAnswer
                    )}
              </div>
            </div>
            <Button
              variant='success'
              className='telephony-drawer__ringing-answer'
              disabled={runningAction === 'resume'}
              onClick={() => void handleResumeAgent()}
            >
              <i className='bi bi-telephone-fill me-2' />
              {intl.formatMessage(runningAction === 'resume' ? M.connecting : M.answer)}
            </Button>
          </section>
        )}
        {canOperateVicidial && (
          <section className='telephony-drawer__section telephony-drawer__section--controls'>
            <div className='telephony-drawer__section-head'>
              <div>
                <div className='telephony-drawer__section-kicker'>
                  {intl.formatMessage(M.agentControls)}
                </div>
                <div className='telephony-drawer__section-title'>
                  {intl.formatMessage(M.agentControlsSub)}
                </div>
              </div>
              {telephony.vicidialSession.callsInQueueCount > 0 && (
                <Badge bg='info' className='text-uppercase telephony-drawer__queue-badge'>
                  <i className='bi bi-people-fill me-1' />
                  {intl.formatMessage(M.inQueue, {
                    count: telephony.vicidialSession.callsInQueueCount,
                  })}
                </Badge>
              )}
            </div>
            <div className='telephony-drawer__controls-row'>
              <div className='telephony-drawer__field-group'>
                <label className='telephony-drawer__field-label'>
                  {intl.formatMessage(M.pauseReason)}
                </label>
                <Form.Select
                  size='sm'
                  value={pauseCodeInput}
                  onChange={(event) => setPauseCodeInput(event.target.value)}
                  disabled={disconnecting || telephony.vicidialSession.connecting || isPausedState}
                  className='telephony-drawer__input telephony-drawer__input--compact'
                >
                  <option value=''>{intl.formatMessage(M.selectReason)}</option>
                  {pauseCodeOptions.map((opt) => (
                    <option key={opt.code} value={opt.code}>
                      {opt.label}
                    </option>
                  ))}
                </Form.Select>
              </div>
              <Button
                variant={pauseActionVariant}
                size='sm'
                onClick={isPausedState ? handleResumeAgent : handlePauseAgent}
                disabled={!canTogglePauseState || pauseCodeRequiredButMissing}
                className='telephony-drawer__primary-button'
              >
                {isPauseActionRunning || isResumeActionRunning
                  ? pauseActionBusyLabel
                  : pauseActionLabel}
              </Button>
            </div>
            {/* Blind transfer to a colleague in my group or a queue. The picker
                shows live availability (green=available, amber=on a call,
                gray=offline). Hidden on autodial campaigns. */}
            {isManualCampaign && (
            <div className='telephony-drawer__field-group telephony-drawer__field-group--stretch'>
              <div className='telephony-drawer__field-label-row'>
                <label className='telephony-drawer__field-label'>
                  {intl.formatMessage(M.transferCall)}
                </label>
                <Button
                  variant='link'
                  size='sm'
                  className='telephony-drawer__inline-link'
                  onClick={() => void telephony.loadTransferTargets()}
                  disabled={telephony.loadingTransferTargets}
                >
                  {intl.formatMessage(
                    telephony.loadingTransferTargets ? M.refreshing : M.refresh
                  )}
                </Button>
              </div>
              <div className='telephony-drawer__inline-form'>
                <Form.Select
                  size='sm'
                  value={transferDestination}
                  onChange={(event) => setTransferDestination(event.target.value)}
                  disabled={
                    disconnecting ||
                    telephony.vicidialSession.connecting ||
                    !telephony.vicidialSession.activeSession
                  }
                  className='telephony-drawer__input'
                >
                  <option value=''>{intl.formatMessage(M.selectDestination)}</option>
                  {transferAgentOptions.length > 0 && (
                    <optgroup label={intl.formatMessage(M.agents)}>
                      {transferAgentOptions.map((agent) => (
                        <option
                          key={`agent-${agent.user_id}`}
                          value={`AGENT:${agent.agent_user}`}
                          disabled={!agent.agent_user}
                        >
                          {transferAgentStatusSymbol(agent.live)} {agent.name}
                          {!agent.live.logged_in
                            ? intl.formatMessage(M.agentOffline)
                            : agent.live.available
                            ? ''
                            : intl.formatMessage(M.agentOnCall)}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {transferQueueOptions.length > 0 && (
                    <optgroup label={intl.formatMessage(M.queues)}>
                      {transferQueueOptions.map((queue) => (
                        <option key={`queue-${queue.group_id}`} value={`INGROUP:${queue.group_id}`}>
                          {GLYPH_PHONE} {queue.name}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </Form.Select>
                <Button
                  variant='outline-primary'
                  size='sm'
                  onClick={() => runVicidialDrawerAction('transfer', handleTransferCall)}
                  disabled={
                    disconnecting ||
                    telephony.vicidialSession.connecting ||
                    runningAction === 'transfer' ||
                    !telephony.vicidialSession.activeSession ||
                    !transferDestination
                  }
                  className='telephony-drawer__secondary-button'
                >
                  {intl.formatMessage(runningAction === 'transfer' ? M.sending : M.transfer)}
                </Button>
              </div>
              {transferAgentOptions.length === 0 && transferQueueOptions.length === 0 && (
                <div className='telephony-drawer__field-hint'>
                  {intl.formatMessage(
                    telephony.loadingTransferTargets ? M.loadingDestinations : M.noDestinations
                  )}
                </div>
              )}
            </div>
            )}
            {/* Manual dial: type any number and place a tracked PolicyDial call
                (external_dial) — uses the Caller ID selected above and rings on
                the embedded webphone, same flow as the CRM "Call with PolicyDial".
                Hidden on autodial campaigns (agents there wait for the dialer). */}
            {isManualCampaign && (
            <div className='telephony-drawer__field-group telephony-drawer__field-group--stretch'>
              <label className='telephony-drawer__field-label'>
                {intl.formatMessage(M.manualDial)}
              </label>
              <div className='telephony-drawer__inline-form'>
                <Form.Control
                  size='sm'
                  type='tel'
                  placeholder={intl.formatMessage(M.numberToDial)}
                  value={manualPhoneNumber}
                  onChange={(event) => setManualPhoneNumber(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && manualPhoneNumber.trim()) {
                      event.preventDefault()
                      void handleManualCall()
                    }
                  }}
                  disabled={disconnecting || telephony.vicidialSession.connecting}
                  className='telephony-drawer__input'
                />
                <Button
                  variant='primary'
                  size='sm'
                  onClick={() => void handleManualCall()}
                  disabled={
                    disconnecting ||
                    telephony.vicidialSession.connecting ||
                    runningAction === 'dial' ||
                    !manualPhoneNumber.trim()
                  }
                  className='telephony-drawer__secondary-button'
                >
                  {intl.formatMessage(runningAction === 'dial' ? M.calling : M.call)}
                </Button>
              </div>
            </div>
            )}
          </section>
        )}
        {drawerState.url && !shouldForceExternalOpen ? (
          <section className='telephony-drawer__frame-section'>
            <div className='telephony-drawer__frame-toolbar'>
              <div className='telephony-drawer__frame-dots'>
                <span />
                <span />
                <span />
              </div>
              <div className='telephony-drawer__frame-label'>
                {drawerState.title || intl.formatMessage(M.webphone)}
              </div>
              <div className='telephony-drawer__frame-tag'>
                {intl.formatMessage(
                  embeddedWebphoneUrl && isVicidialAutologinBridge ? M.embedded : M.live
                )}
              </div>
            </div>
            <div className='telephony-drawer__frame-shell'>
              {webphoneBlockedByOtherTab ? (
                <div className='telephony-drawer__other-tab'>
                  <div className='telephony-drawer__other-tab-icon'>
                    <i className='bi bi-window-stack' />
                  </div>
                  <div className='telephony-drawer__other-tab-title'>
                    {intl.formatMessage(M.otherTabTitle)}
                  </div>
                  <div className='telephony-drawer__other-tab-copy'>
                    {intl.formatMessage(M.otherTabCopy)}
                  </div>
                  <Button
                    variant='primary'
                    size='sm'
                    disabled={webphoneOwnership.ownerInCall}
                    onClick={() => webphoneOwnership.requestTakeover()}
                  >
                    <i className='bi bi-arrow-left-right me-2' />
                    {intl.formatMessage(M.useItHere)}
                  </Button>
                  {webphoneOwnership.ownerInCall && (
                    <div className='telephony-drawer__other-tab-note'>
                      {intl.formatMessage(M.finishOtherCall)}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <iframe
                    src={drawerState.url}
                    title={drawerState.title || intl.formatMessage(M.webphone)}
                    allow='microphone; autoplay'
                    referrerPolicy='no-referrer'
                    // Block manual interaction with the agent webphone (typing on
                    // its dialpad deregisters the session). Agents dial from
                    // PolicyTools. Supervisor 'agent' screen stays interactive.
                    style={isAgentScreenMode ? undefined : {pointerEvents: 'none'}}
                    className={
                      embeddedWebphoneUrl && isVicidialAutologinBridge
                        ? 'telephony-drawer__iframe telephony-drawer__iframe--bridge-hidden'
                        : 'telephony-drawer__iframe'
                    }
                  />
                  {embeddedWebphoneUrl && isVicidialAutologinBridge ? (
                    <iframe
                      src={embeddedWebphoneUrl}
                      title={intl.formatMessage(M.embeddedWebphoneTitle)}
                      allow='microphone; autoplay'
                      referrerPolicy='no-referrer'
                      // Display + audio only; block clicks/typing on its dialpad.
                      style={{pointerEvents: 'none'}}
                      className='telephony-drawer__iframe telephony-drawer__iframe--embedded'
                    />
                  ) : null}
                  {!isAgentScreenMode && (
                    <div className='telephony-drawer__muted-copy text-center' style={{padding: '4px 8px', fontSize: '0.75rem'}}>
                      {intl.formatMessage(M.keypadDisabled)}
                    </div>
                  )}
                </>
              )}
            </div>
          </section>
        ) : (
          <section className='telephony-drawer__frame-section telephony-drawer__frame-section--empty'>
            <div className='telephony-drawer__empty-state'>
              <div className='telephony-drawer__empty-icon'>
                <i className='bi bi-telephone-forward' />
              </div>
              <div className='telephony-drawer__empty-title'>
                {intl.formatMessage(M.waitingForWebphone)}
              </div>
              <div className='telephony-drawer__muted-copy'>
                {intl.formatMessage(M.waitingForWebphoneCopy)}
              </div>
            </div>
          </section>
        )}
      </div>
      <AudioDeviceCheck show={showAudioCheck} onClose={() => setShowAudioCheck(false)} />
    </div>
  )
}
